"use client";

import PageAnimatePresence from "@/components/organisms/HOC/pageAnimatePresence";
import { ContextWrapper } from "@/context/state";
import { AnimatePresence } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import WhatsAppLogo from "@/components/atoms/waLogo";

/**
 * Wrapper component that provides context and animates presence.
 *
 * @component
 * @example
 * return (
 *   <Wrapper>
 *     <YourComponent />
 *   </Wrapper>
 * )
 * @param {object} props - The props for the component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the wrapper.
 * @returns {JSX.Element} The wrapper component with context and animation.
 */
const Wrapper = ({ children }) => {
  const pathname = usePathname();

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();

    document.addEventListener("contextmenu", handleContextMenu);

    // Cleanup event listener on component unmount
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  const isCampPage = pathname.startsWith("/tennis-camp") || pathname.startsWith("/padel-camp")
  const isBarePage = pathname.startsWith("/admin") || pathname.startsWith("/tv")

  return !isBarePage ? (
    <ContextWrapper>
      <PageAnimatePresence className={`w-full h-full`}>
        {children}
      </PageAnimatePresence>
      {/* Mobile: Book on Playtomic + WhatsApp pill buttons */}
      <div className={`md:hidden fixed bottom-6 right-6 z-50 flex items-center gap-3 ${isCampPage ? 'hidden' : ''}`}>
        <a
          href="https://app.playtomic.io/tenants/325afbbf-2acc-473b-b93c-0dd30b4adff3/reservations"
          target="_blank"
          rel="noopener noreferrer"
          className="h-14 flex items-center px-5 bg-olive hover:bg-umber text-background font-chivoMono uppercase tracking-[1.5px] text-[12px] shadow-lg rounded-full whitespace-nowrap transition-colors duration-300"
        >
          Book on Playtomic
        </a>
        <a
          href="https://wa.me/6281337582672"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="w-14 h-14 flex items-center justify-center bg-olive hover:bg-umber rounded-full shadow-lg shrink-0 transition-colors duration-300"
        >
          <WhatsAppLogo style={{ width: '28px', height: '28px' }} />
        </a>
      </div>

      {/* Desktop: WhatsApp with hover expand */}
      <a
        href="https://wa.me/6281337582672"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className={`fixed bottom-6 right-6 z-50 group flex-row-reverse items-center h-14 bg-olive hover:bg-umber rounded-full shadow-lg transition-colors duration-300 ${isCampPage ? 'hidden' : 'hidden md:flex'}`}
      >
        <div className="w-14 h-14 flex items-center justify-center shrink-0">
          <WhatsAppLogo style={{ width: '28px', height: '28px' }} />
        </div>
        <div className="max-w-0 group-hover:max-w-[120px] overflow-hidden transition-all duration-300 ease-in-out">
          <span className="block whitespace-nowrap text-dune font-chivoMono uppercase tracking-widest text-[11px] pl-4 pr-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 delay-150 select-none">
            Contact us
          </span>
        </div>
      </a>
    </ContextWrapper>
  ) : (
    children
  );
};

export default Wrapper;
